import { createContext, useDisclosure } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React, { useMemo, useState } from 'react';

import { DEFAULT_PALETTE_NAME } from '../../constants';
import { getImageAspect } from '../../helpers';
import type {
  DesignerMaterialProduct,
  DesignerPaintProduct,
} from '../../types';
import {
  useFirstAvailablePaletteFallback,
  useMaterials,
  usePaintColors,
  usePaletteSelection,
} from '../Palette/hooks';

import { useCustomization } from './useCustomization';

export interface PaletteContext {
  selectedPalette: string;
  hasUserSelectedPalette: boolean;
  setSelectedPalette: (palette: string) => void;
  onSelectPaletteOpen: () => void;
  onSelectPaletteClose: () => void;
  isLoading: boolean;
  isSelectPaletteOpen: boolean;
  materials: DesignerMaterialProduct[];
  paintColors: DesignerPaintProduct[];
  palettePaintColors: DesignerPaintProduct[];
  paletteMaterials: DesignerMaterialProduct[];
}

const [Provider, useContext] = createContext<PaletteContext>({
  name: 'PalettesContext',
});

export const usePalettes = useContext;

export interface PaletteSelectionProviderProps extends PropsWithChildren<{}> {}

export const PaletteProvider = ({
  children,
}: PaletteSelectionProviderProps) => {
  const { category, targetImage } = useCustomization();

  // Selected palette

  const [selectedPaintPalette, setSelectedPaintPalette] =
    useState<string>(DEFAULT_PALETTE_NAME);
  const [selectedMaterialPalette, setSelectedMaterialPalette] =
    useState<string>('Brick');

  const [hasUserSelectedPaintPalette, setHasUserSelectedPaintPalette] =
    useState(false);
  const [hasUserSelectedMaterialPalette, setHasUserSelectedMaterialPalette] =
    useState(false);

  const defaultState = { defaultIsOpen: true };

  const paintSelectPaletteState = useDisclosure(defaultState);
  const materialSelectPaletteState = useDisclosure(defaultState);

  const aspect = getImageAspect(targetImage);

  // Materials

  const [materials, { loading: isLoadingMaterials }] = useMaterials({
    aspect,
    category: category ?? undefined,
    include3dFusionMaterials: targetImage.aiGeneratedImage.is3dFusionCompatible,
  });

  const paletteMaterials = usePaletteSelection(
    materials,
    selectedMaterialPalette,
  );

  useFirstAvailablePaletteFallback(
    materials,
    paletteMaterials,
    selectedMaterialPalette,
    setSelectedMaterialPalette,
  );

  // Colors

  const [paintColors, { loading: isLoadingPaintColors }] = usePaintColors({
    aspect,
    category: category ?? undefined,
  });
  const palettePaintColors = usePaletteSelection(
    paintColors,
    selectedPaintPalette,
  );

  useFirstAvailablePaletteFallback(
    paintColors,
    palettePaintColors,
    selectedPaintPalette,
    setSelectedPaintPalette,
  );

  const value = useMemo(() => {
    const products = {
      materials,
      paintColors,
      palettePaintColors,
      paletteMaterials,
    };

    if (!category) {
      return {
        isLoading: true,
        selectedPalette: '',
        hasUserSelectedPalette: false,
        setSelectedPalette: () => {},
        onSelectPaletteOpen: () => {},
        onSelectPaletteClose: () => {},
        isSelectPaletteOpen: false,
        ...products,
      };
    }

    const { type } = category;

    const setHasUserSelectedPalette =
      type === 'paint'
        ? setHasUserSelectedPaintPalette
        : setHasUserSelectedMaterialPalette;
    const selectedPalette =
      type === 'paint' ? selectedPaintPalette : selectedMaterialPalette;
    const hasUserSelectedPalette =
      type === 'paint'
        ? hasUserSelectedPaintPalette
        : hasUserSelectedMaterialPalette;

    const isLoading =
      type === 'paint' ? isLoadingPaintColors : isLoadingMaterials;
    const setSelectedPalette =
      type === 'paint' ? setSelectedPaintPalette : setSelectedMaterialPalette;

    const {
      onClose: onSelectPaletteClose,
      onOpen: onSelectPaletteOpen,
      isOpen: isSelectPaletteOpen,
    } = type === 'paint' ? paintSelectPaletteState : materialSelectPaletteState;

    return {
      selectedPalette,
      hasUserSelectedPalette,
      setSelectedPalette: (palette: string) => {
        setHasUserSelectedPalette(true);
        setSelectedPalette(palette);
      },
      onSelectPaletteOpen,
      onSelectPaletteClose,
      isLoading,
      isSelectPaletteOpen,
      ...products,
    };
  }, [
    category,
    hasUserSelectedMaterialPalette,
    hasUserSelectedPaintPalette,
    isLoadingMaterials,
    isLoadingPaintColors,
    materialSelectPaletteState,
    materials,
    paintColors,
    paintSelectPaletteState,
    paletteMaterials,
    palettePaintColors,
    selectedMaterialPalette,
    selectedPaintPalette,
  ]);

  return <Provider value={value}>{children}</Provider>;
};
